<template>
    <div class="wechat-scheduled-page">
        <div class="page-container">
            <div class="settings-form">
                <!-- 页面标题和统计 -->
                <div class="header-section">
                    <h2 class="page-title">微信模板消息定时任务</h2>
                    <div class="stats-section">
                        <el-row :gutter="20">
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon total">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_tasks || 0 }}</div>
                                        <div class="stat-label">总任务数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon active">
                                        <el-icon>
                                            <CircleCheck />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.active_tasks || 0 }}</div>
                                        <div class="stat-label">运行中</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon success">
                                        <el-icon>
                                            <TrendCharts />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_deliveries || 0 }}</div>
                                        <div class="stat-label">总发送次数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon today">
                                        <el-icon>
                                            <Clock />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.today_deliveries || 0 }}</div>
                                        <div class="stat-label">今日发送</div>
                                    </div>
                                </div>
                            </el-col>
                        </el-row>
                    </div>
                </div>

                <!-- 操作按钮区域 -->
                <div class="action-bar">
                    <el-button type="primary" size="large" @click="openCreateDialog">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        新建定时任务
                    </el-button>
                    <el-button type="success" size="large" @click="fetchTaskList">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        刷新列表
                    </el-button>
                    <el-button type="info" size="large" @click="openLogsDialog">
                        <el-icon>
                            <Document />
                        </el-icon>
                        查看发送日志
                    </el-button>
                </div>

                <!-- 任务列表 -->
                <div class="task-list-section">
                    <el-table :data="taskList" stripe v-loading="loading" style="width: 100%">
                        <el-table-column prop="task_name" label="任务名称" min-width="150" show-overflow-tooltip />

                        <el-table-column label="关联用户" width="150">
                            <template #default="{ row }">
                                <div class="user-info">
                                    <div class="user-nickname">{{ getUserInfo(row.user_id).nickname }}</div>
                                    <div class="user-phone">{{ getUserInfo(row.user_id).phone }}</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column prop="account_name" label="公众号账户" width="150" show-overflow-tooltip />
                        <el-table-column prop="appid" label="AppID" width="180" show-overflow-tooltip />

                        <el-table-column label="消息模板" width="200" show-overflow-tooltip>
                            <template #default="{ row }">
                                <div class="template-info">
                                    <div class="template-name">{{ row.template_name }}</div>
                                    <div class="template-id">ID: {{ row.template_id }}</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="用户选择" width="150">
                            <template #default="{ row }">
                                <div class="user-selection-info">
                                    <div v-if="row.select_all" class="selection-type">
                                        <el-tag size="small" type="success">全选用户</el-tag>
                                    </div>
                                    <div v-else-if="row.openids && row.openids.length > 0" class="selection-type">
                                        <el-tag size="small" type="primary">指定用户</el-tag>
                                        <div class="count">({{ row.openids.length }}人)</div>
                                    </div>
                                    <div v-else-if="row.tag_ids && row.tag_ids.length > 0" class="selection-type">
                                        <el-tag size="small" type="warning">标签筛选</el-tag>
                                        <div class="count">({{ row.tag_ids.length }}标签)</div>
                                    </div>
                                    <div v-else class="selection-type">
                                        <el-tag size="small" type="info">未配置</el-tag>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="发送配置" width="150">
                            <template #default="{ row }">
                                <div class="delivery-config">
                                    <div>每次: {{ row.send_count_per_delivery }} 条</div>
                                    <div>时段: {{ row.time_periods?.length || 0 }} 个</div>
                                    <div>星期: {{ row.weekdays?.length || 0 }} 天</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="任务状态" width="100">
                            <template #default="{ row }">
                                <el-tag :type="row.status ? 'success' : 'info'" effect="light">
                                    {{ row.status ? '运行中' : '已停用' }}
                                </el-tag>
                                <div v-if="row.next_delivery_time" class="next-time">
                                    {{ formatTime(row.next_delivery_time) }}
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="发送统计" width="120">
                            <template #default="{ row }">
                                <div class="delivery-stats">
                                    <div>成功: {{ row.success_delivery_count || 0 }}</div>
                                    <div>总计: {{ row.total_delivery_count || 0 }}</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="最后发送" width="180">
                            <template #default="{ row }">
                                <span v-if="row.last_delivery_time">{{ formatTime(row.last_delivery_time) }}</span>
                                <span v-else class="empty-value">暂无发送</span>
                            </template>
                        </el-table-column>

                        <el-table-column label="操作" width="400" fixed="right">
                            <template #default="{ row }">
                                <div class="action-buttons">
                                    <!-- 复制按钮 -->
                                    <el-button type="success" size="small" @click="copyTask(row)">
                                        <el-icon>
                                            <CopyDocument />
                                        </el-icon>
                                        复制
                                    </el-button>

                                    <el-button type="primary" size="small" @click="openEditDialog(row)">
                                        <el-icon>
                                            <Edit />
                                        </el-icon>
                                        编辑
                                    </el-button>
                                    <el-button :type="row.status ? 'warning' : 'success'" size="small"
                                        @click="toggleTaskStatus(row)">
                                        <el-icon v-if="row.status">
                                            <VideoPause />
                                        </el-icon>
                                        <el-icon v-else>
                                            <CircleCheck />
                                        </el-icon>
                                        {{ row.status ? '暂停' : '启用' }}
                                    </el-button>
                                    <el-button type="danger" size="small" @click="deleteTask(row.id)">
                                        <el-icon>
                                            <Delete />
                                        </el-icon>
                                        删除
                                    </el-button>
                                    <el-button type="info" size="small" @click="manualDelivery(row)">
                                        <el-icon>
                                            <Promotion />
                                        </el-icon>
                                        投放
                                    </el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>

                    <!-- 分页 -->
                    <div class="pagination-container">
                        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                            :total="pagination.total" :page-sizes="[10, 20, 50, 100]"
                            layout="total, sizes, prev, pager, next, jumper" @size-change="fetchTaskList"
                            @current-change="fetchTaskList" />
                    </div>
                </div>
            </div>
        </div>

        <!-- 创建/编辑任务弹窗 -->
        <el-dialog :title="isEditMode ? '编辑微信定时任务' : '新建微信定时任务'" v-model="showCreateDialog" width="900px"
            :close-on-click-modal="false" @close="resetTaskForm">

            <el-form :model="taskForm" label-width="140px" ref="taskFormRef" class="task-form">
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="任务名称" prop="task_name"
                            :rules="[{ required: true, message: '请输入任务名称', trigger: 'blur' }]">
                            <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="任务状态">
                            <el-switch v-model="taskForm.status" active-text="启用" inactive-text="停用" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <!-- 公众号选择 -->
                <el-form-item label="选择公众号" prop="account_id"
                    :rules="[{ required: true, message: '请选择公众号账户', trigger: 'change' }]">
                    <el-select v-model="taskForm.account_id" placeholder="请选择公众号账户" style="width: 100%" clearable
                        @change="handleAccountChange">
                        <el-option v-for="account in accountList" :key="account.id"
                            :label="`${account.account_name} (${account.appid})`" :value="account.id" />
                    </el-select>
                </el-form-item>

                <!-- 消息模板选择 -->
                <el-form-item label="选择消息模板" prop="template_id"
                    :rules="[{ required: true, message: '请选择消息模板', trigger: 'change' }]">
                    <el-select v-model="taskForm.template_id" placeholder="请选择消息模板" style="width: 100%" clearable
                        @change="handleTemplateChange">
                        <el-option v-for="template in templateList" :key="template.template_id"
                            :label="`${template.title} (${template.template_id})`" :value="template.template_id" />
                    </el-select>

                    <!-- 模板预览 -->
                    <div v-if="selectedTemplate" class="template-preview">
                        <el-collapse>
                            <el-collapse-item title="模板内容预览">
                                <div class="preview-content">
                                    <div class="template-title"><strong>标题:</strong> {{ selectedTemplate.title }}</div>
                                    <div class="template-content">
                                        <strong>内容:</strong>
                                        <div v-html="formatTemplateContent(selectedTemplate.content)"></div>
                                    </div>
                                    <div v-if="selectedTemplate.example" class="template-example">
                                        <strong>示例:</strong>
                                        <div>{{ selectedTemplate.example }}</div>
                                    </div>
                                </div>
                            </el-collapse-item>
                        </el-collapse>
                    </div>
                </el-form-item>

                <!-- 关键词填写 -->
                <el-form-item label="模板关键词" prop="keywords" v-if="templateKeywords.length > 0"
                    :rules="[{ required: true, message: '请填写所有关键词', trigger: 'blur' }]">
                    <div class="keywords-container">
                        <div v-for="keyword in templateKeywords" :key="keyword" class="keyword-item">
                            <el-input v-model="taskForm.keywords[keyword]" :placeholder="`请输入${keyword}`"
                                @focus="autoGenerateString(keyword)">
                                <template #prepend>{{ keyword }}</template>
                                <template #append>
                                    <el-button @click="autoGenerateString(keyword)" title="自动生成">
                                        <el-icon>
                                            <Refresh />
                                        </el-icon>
                                    </el-button>
                                </template>
                            </el-input>
                        </div>
                    </div>
                </el-form-item>

                <!-- 跳转链接 -->
                <el-form-item label="跳转链接">
                    <el-input v-model="taskForm.url" placeholder="请输入跳转链接（可选）" />
                    <div class="form-tip">用户点击消息时可以跳转到此链接</div>
                </el-form-item>

                <!-- 用户选择配置 -->
                <el-form-item label="用户选择" prop="user_selection"
                    :rules="[{ required: true, message: '请选择用户', trigger: 'change' }]">
                    <div class="user-selection-container">
                        <el-radio-group v-model="taskForm.select_all" @change="handleUserSelectionChange">
                            <el-radio :label="1">全选所有用户</el-radio>
                            <el-radio :label="0">自定义选择</el-radio>
                        </el-radio-group>

                        <!-- 自定义选择 -->
                        <div v-if="taskForm.select_all === 0" class="custom-selection">
                            <div class="selection-tabs">
                                <el-radio-group v-model="userSelectionMode" size="small">
                                    <el-radio-button label="tags">按标签选择</el-radio-button>
                                    <el-radio-button label="openids">指定OpenID</el-radio-button>
                                </el-radio-group>
                            </div>

                            <!-- 按标签选择 -->
                            <div v-if="userSelectionMode === 'tags'" class="tags-selection">
                                <div class="tags-header">
                                    <span>选择用户标签</span>
                                    <span class="selected-count">已选: {{ taskForm.tag_ids?.length || 0 }} 个标签</span>
                                </div>
                                <div class="tags-list">
                                    <el-checkbox-group v-model="taskForm.tag_ids">
                                        <el-checkbox v-for="tag in tags" :key="tag.tag_id" :label="tag.tag_id"
                                            class="tag-checkbox">
                                            {{ tag.tag_name }} ({{ tag.user_count }})
                                        </el-checkbox>
                                    </el-checkbox-group>
                                </div>
                                <div v-if="!tags.length" class="empty-tags">
                                    <el-alert type="warning" show-icon :closable="false">
                                        该公众号暂无标签，请先同步标签数据
                                    </el-alert>
                                </div>
                            </div>

                            <!-- 指定OpenID -->
                            <div v-if="userSelectionMode === 'openids'" class="openids-selection">
                                <div class="openids-header">
                                    <span>指定OpenID</span>
                                    <el-button type="primary" size="small" @click="openUserSelector">
                                        <el-icon>
                                            <Select />
                                        </el-icon>
                                        选择用户
                                    </el-button>
                                </div>
                                <el-input type="textarea" v-model="openidText" placeholder="每行一个OpenID" :rows="5"
                                    @input="handleOpenidInput" />
                                <div class="form-tip">支持手动输入或点击"选择用户"按钮从列表中选择</div>

                                <!-- 已选择用户展示 -->
                                <div v-if="selectedUserList.length > 0" class="selected-users">
                                    <div class="selected-header">
                                        <span>已选择 {{ selectedUserList.length }} 个用户</span>
                                        <el-button type="text" @click="clearSelectedUsers">清空</el-button>
                                    </div>
                                    <div class="user-tags">
                                        <el-tag v-for="user in selectedUserList" :key="user.openid" closable
                                            @close="removeSelectedUser(user.openid)">
                                            {{ user.remark || user.openid }}
                                        </el-tag>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- 每次发送数量 -->
                <el-form-item label="每次发送数量" prop="send_count_per_delivery"
                    :rules="[{ required: true, message: '请输入每次发送数量', trigger: 'blur' }]">
                    <el-input-number v-model="taskForm.send_count_per_delivery" :min="1" :max="1000"
                        controls-position="right" />
                    <div class="form-tip">每次任务执行时发送的消息数量，最多1000条</div>
                </el-form-item>

                <!-- 星期选择 -->
                <el-form-item label="发送星期" prop="weekdays"
                    :rules="[{ required: true, message: '请选择发送星期', trigger: 'change' }]">
                    <div class="weekdays-container">
                        <el-checkbox-group v-model="taskForm.weekdays">
                            <el-checkbox v-for="day in weekdayOptions" :key="day.value" :label="day.value">
                                {{ day.label }}
                            </el-checkbox>
                        </el-checkbox-group>
                        <div class="form-tip">选择消息发送的星期，未选择的星期将不发送</div>
                    </div>
                </el-form-item>

                <!-- 发送时间段 -->
                <el-form-item label="发送时间段" prop="time_periods"
                    :rules="[{ required: true, message: '请设置发送时间段', trigger: 'change' }]">
                    <div class="time-periods-container">
                        <div class="time-periods-header">
                            <span>时间段配置</span>
                            <el-button type="primary" text @click="addTimePeriod">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                                添加时间段
                            </el-button>
                        </div>

                        <div class="time-periods-list">
                            <div v-for="(period, index) in taskForm.time_periods" :key="index" class="period-item">
                                <div class="period-time">
                                    <el-time-picker v-model="period.start_time" placeholder="开始时间" format="HH:mm"
                                        value-format="HH:mm" style="width: 120px" />
                                    <span class="time-separator">至</span>
                                    <el-time-picker v-model="period.end_time" placeholder="结束时间" format="HH:mm"
                                        value-format="HH:mm" style="width: 120px" />
                                </div>
                                <div class="period-count">
                                    <el-input-number v-model="period.max_deliveries" :min="1" :max="100"
                                        placeholder="最大次数" controls-position="right" />
                                    <span class="count-label">次/时段</span>
                                </div>
                                <el-button text @click="removeTimePeriod(index)"
                                    :disabled="taskForm.time_periods.length === 1">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                </el-button>
                            </div>
                        </div>

                        <div class="form-tip">系统将在设定的时间段内自动发送消息，每个时段不超过设定的最大次数</div>
                    </div>
                </el-form-item>

                <!-- 备注 -->
                <el-form-item label="备注信息">
                    <el-input v-model="taskForm.notes" type="textarea" :rows="3" placeholder="请输入任务备注信息" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="showCreateDialog = false">取消</el-button>
                <el-button type="primary" @click="submitTask" :loading="submitting">
                    {{ isEditMode ? '更新任务' : '创建任务' }}
                </el-button>
            </template>
        </el-dialog>

        <!-- 用户选择器弹窗 -->
        <el-dialog v-model="showUserSelectorDialog" title="选择用户" width="800px" class="user-selector-dialog">
            <WechatUserSelector v-if="showUserSelectorDialog" :account-id="taskForm.account_id" :store-id="userInfo.id"
                :pre-selected-openids="taskForm.openids" @confirm="handleUserSelectionConfirm"
                @cancel="showUserSelectorDialog = false" />
        </el-dialog>

        <!-- 发送日志弹窗 -->
        <el-dialog title="微信消息发送日志" v-model="showLogsDialog" width="1050px" top="5vh" @open="fetchDeliveryLogs">
            <div class="logs-content">
                <div class="logs-filter">
                    <el-form :inline="true" :model="logsFilter">
                        <el-form-item label="任务">
                            <el-select v-model="logsFilter.task_id" placeholder="选择任务" clearable style="width:150px">
                                <el-option v-for="task in taskList" :key="task.id" :label="task.task_name"
                                    :value="task.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="日期">
                            <el-date-picker v-model="logsFilter.date_range" type="daterange" range-separator="至"
                                start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="fetchDeliveryLogs">搜索</el-button>
                            <el-button @click="resetLogsFilter">重置</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <el-table :data="deliveryLogs" v-loading="logsLoading" height="500">
                    <el-table-column prop="task_name" label="任务名称" width="150" />
                    <el-table-column prop="account_name" label="公众号账户" width="150" />
                    <el-table-column prop="template_name" label="消息模板" width="200" show-overflow-tooltip />
                    <el-table-column label="用户选择" width="120">
                        <template #default="{ row }">
                            <div v-if="row.tag_ids && row.tag_ids.length > 0">
                                <el-tag size="small">标签</el-tag>
                            </div>
                            <div v-else-if="row.openids && row.openids.length > 0">
                                <el-tag size="small">指定用户</el-tag>
                            </div>
                            <div v-else>
                                <el-tag size="small">全选</el-tag>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="发送数量" width="120">
                        <template #default="{ row }">
                            {{ row.delivery_count }} 条
                        </template>
                    </el-table-column>
                    <el-table-column label="发送结果" width="120">
                        <template #default="{ row }">
                            <el-tag :type="row.fail_count > 0 ? 'warning' : 'success'">
                                成功: {{ row.success_count }} 失败: {{ row.fail_count }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="delivery_time" label="发送时间" width="180">
                        <template #default="{ row }">
                            {{ formatTime(row.delivery_time) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="100">
                        <template #default="{ row }">
                            <el-button type="primary" text @click="viewLogDetail(row)">详情</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="pagination-container">
                    <el-pagination v-model:current-page="logsPagination.page"
                        v-model:page-size="logsPagination.pageSize" :total="logsPagination.total"
                        :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
                        @size-change="fetchDeliveryLogs" @current-change="fetchDeliveryLogs" />
                </div>
            </div>
        </el-dialog>

        <!-- 日志详情弹窗 -->
        <el-dialog title="消息发送详情" v-model="showLogDetailDialog" width="600px">
            <div v-if="currentLogDetail" class="log-detail">
                <el-descriptions :column="1" border>
                    <el-descriptions-item label="任务名称">{{ currentLogDetail.task_name }}</el-descriptions-item>
                    <el-descriptions-item label="公众号账户">{{ currentLogDetail.account_name }}</el-descriptions-item>
                    <el-descriptions-item label="消息模板">{{ currentLogDetail.template_name }}</el-descriptions-item>
                    <el-descriptions-item label="发送时间">{{ formatTime(currentLogDetail.delivery_time)
                        }}</el-descriptions-item>
                    <el-descriptions-item label="发送结果">
                        成功: {{ currentLogDetail.success_count }} 条,
                        失败: {{ currentLogDetail.fail_count }} 条
                    </el-descriptions-item>
                    <el-descriptions-item label="错误信息" v-if="currentLogDetail.error_message">
                        <el-alert :title="currentLogDetail.error_message" type="error" :closable="false" />
                    </el-descriptions-item>
                </el-descriptions>

                <div v-if="currentLogDetail.details && currentLogDetail.details.fail_list" class="fail-list">
                    <h4>失败用户列表:</h4>
                    <el-tag v-for="(fail, index) in currentLogDetail.details.fail_list" :key="index" type="danger"
                        class="fail-tag">
                        {{ fail.openid }}: {{ fail.errmsg }}
                    </el-tag>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Delete, Refresh, Document,
    CircleCheck, Clock,
    VideoPause, Promotion, Plus,
    Edit, TrendCharts, CopyDocument,
    Select
} from '@element-plus/icons-vue'
import WechatUserSelector from '@/components/WechatUserSelector.vue'

export default {
    name: 'WechatScheduled',
    components: {
        Delete, Refresh, Document,
        CircleCheck, Clock,
        VideoPause, Promotion, Plus,
        Edit, TrendCharts, CopyDocument,
        Select,
        WechatUserSelector
    },
    data() {
        return {
            loading: false,
            submitting: false,
            logsLoading: false,
            userList: [], // 用户列表
            userMap: {},  // 用户ID映射

            // 任务列表数据
            taskList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 统计信息
            stats: {},

            // 弹窗控制
            showCreateDialog: false,
            showLogsDialog: false,
            showLogDetailDialog: false,
            showUserSelectorDialog: false,
            isEditMode: false,

            // 表单数据
            taskForm: {
                task_name: '',
                account_id: '',
                template_id: '',
                keywords: {},
                url: '',
                select_all: 0, // 0:自定义选择, 1:全选所有用户
                tag_ids: [],
                openids: [],
                send_count_per_delivery: 10,
                time_periods: [
                    { start_time: '09:00', end_time: '12:00', max_deliveries: 3 },
                    { start_time: '14:00', end_time: '18:00', max_deliveries: 3 }
                ],
                weekdays: [1, 2, 3, 4, 5], // 默认周一到周五
                status: true,
                notes: ''
            },

            // 用户选择模式
            userSelectionMode: 'tags',

            // 星期选项
            weekdayOptions: [
                { label: '星期一', value: 1 },
                { label: '星期二', value: 2 },
                { label: '星期三', value: 3 },
                { label: '星期四', value: 4 },
                { label: '星期五', value: 5 },
                { label: '星期六', value: 6 },
                { label: '星期日', value: 7 }
            ],

            // 选项数据
            accountList: [],
            templateList: [],
            tags: [],
            selectedTemplate: null,
            openidText: '',
            selectedUserList: [],

            // 日志相关
            deliveryLogs: [],
            currentLogDetail: null,
            logsFilter: {
                task_id: '',
                date_range: []
            },
            logsPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        },
        templateKeywords() {
            if (!this.selectedTemplate) return [];
            const content = this.selectedTemplate.content;
            const matches = content.match(/{{(.*?)\.DATA}}/g) || [];
            return [...new Set(matches.map(match => match.replace(/{{(.*?)\.DATA}}/, '$1').trim()))];
        }
    },
    watch: {
        'taskForm.account_id': {
            async handler(newVal) {
                if (newVal) {
                    await this.fetchTemplates();
                    await this.fetchTags();
                } else {
                    this.templateList = [];
                    this.tags = [];
                    this.selectedTemplate = null;
                }
            }
        }
    },
    mounted() {
        this.fetchTaskList();
        this.fetchUserList();
        this.fetchStats();
        this.fetchAccountList();
    },
    methods: {
        async copyTask(task) {
            this.isEditMode = false;

            // 先设置账户ID，触发获取模板和标签
            this.taskForm.account_id = task.account_id;

            // 等待数据加载完成
            await Promise.all([
                this.fetchTemplates(),
                this.fetchTags()
            ]);

            // 复制任务数据
            this.taskForm = {
                task_name: `${task.task_name}_副本`,
                account_id: task.account_id,
                template_id: task.template_id,
                keywords: task.keywords ? (typeof task.keywords === 'string' ? JSON.parse(task.keywords) : task.keywords) : {},
                url: task.url || '',
                select_all: task.select_all || 0,
                tag_ids: task.tag_ids ? (typeof task.tag_ids === 'string' ? JSON.parse(task.tag_ids) : task.tag_ids) : [],
                openids: task.openids ? (typeof task.openids === 'string' ? JSON.parse(task.openids) : task.openids) : [],
                send_count_per_delivery: task.send_count_per_delivery || 10,
                time_periods: typeof task.time_periods === 'string'
                    ? JSON.parse(task.time_periods)
                    : [...task.time_periods],
                weekdays: typeof task.weekdays === 'string'
                    ? JSON.parse(task.weekdays)
                    : [...task.weekdays],
                status: true, // 复制后默认启用
                notes: task.notes ? `${task.notes} (副本)` : ''
            };

            // 设置选中的模板
            if (task.template_id) {
                const template = this.templateList.find(t => t.template_id === task.template_id);
                this.selectedTemplate = template;
            }

            // 设置用户选择显示
            if (this.taskForm.select_all === 1) {
                this.userSelectionMode = 'all';
            } else if (this.taskForm.tag_ids && this.taskForm.tag_ids.length > 0) {
                this.userSelectionMode = 'tags';
            } else if (this.taskForm.openids && this.taskForm.openids.length > 0) {
                this.userSelectionMode = 'openids';
                this.openidText = this.taskForm.openids.join('\n');
                this.selectedUserList = this.taskForm.openids.slice(0, 10).map(openid => ({ openid }));
            } else {
                this.userSelectionMode = 'tags'; // 默认标签模式
                this.openidText = '';
                this.selectedUserList = [];
            }

            this.showCreateDialog = true;
        },

        // 处理用户选择确认
        handleUserSelectionConfirm(selectedUsers) {
            this.selectedUserList = selectedUsers;
            this.taskForm.openids = selectedUsers.map(user => user.openid);
            this.openidText = this.taskForm.openids.join('\n');
            this.showUserSelectorDialog = false;
            this.$message.success(`已选择 ${selectedUsers.length} 个用户`);
        },

        // 获取用户列表
        async fetchUserList() {
            try {
                const response = await axios.post(
                    `${config.BASE_URL}/shop/api/business_card_management.php`,
                    {
                        action: 'get_users',
                        store_id: this.userInfo.id
                    }
                )

                if (response.data.data) {
                    this.userList = response.data.data
                    // 构建用户映射
                    this.userMap = {}
                    response.data.data.forEach(user => {
                        this.userMap[user.id] = user
                    })
                }
            } catch (error) {
                console.error('获取用户列表错误:', error)
            }
        },

        // 根据user_id获取用户信息
        getUserInfo(userId) {
            const user = this.userMap[userId]
            if (user) {
                return {
                    nickname: user.nickname,
                    phone: user.phone,
                    avatar: user.avatar
                }
            }
            return {
                nickname: '管理员',
                phone: '',
                avatar: ''
            }
        },

        openCreateDialog() {
            this.isEditMode = false;
            this.resetTaskForm();

            // 清空选择数据
            this.selectedTemplate = null;
            this.userSelectionMode = 'tags';
            this.openidText = '';
            this.selectedUserList = [];

            this.showCreateDialog = true;
        },

        // 获取任务列表
        async fetchTaskList() {
            this.loading = true;
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, {
                    action: 'get_tasks',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                });

                if (response.data.data) {
                    this.taskList = response.data.data;
                    this.pagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                this.$message.error('获取任务列表失败');
                console.error('获取任务列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 获取统计信息
        async fetchStats() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, {
                    action: 'get_task_stats',
                    store_id: this.userInfo.id
                });

                if (response.data) {
                    this.stats = response.data;
                }
            } catch (error) {
                console.error('获取统计信息失败:', error);
            }
        },

        // 获取公众号账户列表
        async fetchAccountList() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_account.php`, {
                    action: 'list',
                    store_id: this.userInfo.id,
                    page: 1,
                    page_size: 1000
                });

                if (response.data.data) {
                    this.accountList = response.data.data.filter(account => account.status === 1);
                }
            } catch (error) {
                console.error('获取公众号账户列表失败:', error);
            }
        },

        // 获取模板列表
        async fetchTemplates() {
            if (!this.taskForm.account_id) return;

            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_account.php`, {
                    action: 'search_templates',
                    account_id: this.taskForm.account_id,
                    store_id: this.userInfo.id,
                    page: 1,
                    page_size: 1000
                });

                if (response.data.success) {
                    this.templateList = response.data.data.template_list || [];
                } else {
                    this.$message.error(response.data.error || '获取模板列表失败');
                }
            } catch (error) {
                console.error('获取模板列表失败:', error);
                this.templateList = [];
            }
        },

        // 获取标签列表
        async fetchTags() {
            if (!this.taskForm.account_id) return;

            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_account.php`, {
                    action: 'get_tags',
                    account_id: this.taskForm.account_id,
                    store_id: this.userInfo.id
                });

                if (response.data.success) {
                    this.tags = response.data.data.tags || [];
                }
            } catch (error) {
                console.error('获取标签列表失败:', error);
                this.tags = [];
            }
        },

        // 处理公众号账户变化
        handleAccountChange(accountId) {
            this.taskForm.template_id = '';
            this.selectedTemplate = null;
            this.tags = [];
            this.taskForm.tag_ids = [];
            this.taskForm.openids = [];
            this.selectedUserList = [];
            this.openidText = '';

            if (accountId) {
                this.fetchTemplates();
                this.fetchTags();
            }
        },

        // 处理模板选择变化
        handleTemplateChange(templateId) {
            const template = this.templateList.find(t => t.template_id === templateId);
            this.selectedTemplate = template || null;

            // 重置关键词表单
            if (template) {
                // 初始化关键词
                this.templateKeywords.forEach(keyword => {
                    if (!this.taskForm.keywords[keyword]) {
                        this.taskForm.keywords[keyword] = '';
                    }
                });
            } else {
                this.taskForm.keywords = {};
            }
        },

        // 格式化模板内容
        formatTemplateContent(content) {
            if (!content) return '';
            return content.replace(/{{(.*?)\.DATA}}/g, '<span class="keyword">{$1}</span>').replace(/\n/g, '<br>');
        },

        autoGenerateString(keyword) {
            if (!this.taskForm.keywords[keyword]) {
                let generatedString;
                const keywordLower = keyword.toLowerCase();

                // 检测关键词是否以 "thing" 开头（如 thing4, thing13）
                const isThingFormat = /^thing\d*$/i.test(keyword);

                // 检测关键词是否包含时间相关词汇
                const timePatterns = ['time', 'date', 'timestamp', 'datetime', '时间', '日期'];
                const isTimeKeyword = timePatterns.some(pattern => keywordLower.includes(pattern));

                // 检测关键词是否包含数字相关词汇
                const numberPatterns = ['number', 'num', 'count', 'amount', '数量', '数字', '序号'];
                const isNumberKeyword = numberPatterns.some(pattern => keywordLower.includes(pattern));

                // 检测关键词是否包含金额相关词汇
                const moneyPatterns = ['money', 'price', 'amount', 'fee', '金额', '价格', '费用'];
                const isMoneyKeyword = moneyPatterns.some(pattern => keywordLower.includes(pattern));

                if (isTimeKeyword) {
                    // 生成当前时间：2022-11-28 23:59:59 格式
                    const now = new Date();
                    const year = now.getFullYear();
                    const month = String(now.getMonth() + 1).padStart(2, '0');
                    const day = String(now.getDate()).padStart(2, '0');
                    const hours = String(now.getHours()).padStart(2, '0');
                    const minutes = String(now.getMinutes()).padStart(2, '0');
                    const seconds = String(now.getSeconds()).padStart(2, '0');
                    generatedString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                } else if (isNumberKeyword) {
                    // 生成随机数字
                    generatedString = Math.floor(Math.random() * 10000 + 1).toString();
                } else if (isMoneyKeyword) {
                    // 生成金额格式
                    const amount = (Math.random() * 1000).toFixed(2);
                    generatedString = `¥${amount}`;
                } else if (isThingFormat || keywordLower.includes('thing')) {
                    // 生成有意义的名称（针对 thing 类关键词）
                    const thingNames = [
                        '优质产品', '精选商品', '热门服务', '特惠套餐', '会员权益',
                        '专属礼包', '限量产品', '新品上市', '精品推荐', '热销商品',
                        '智能设备', '家居用品', '办公文具', '数码配件', '健康食品',
                        '时尚服饰', '美妆护肤', '运动器材', '图书音像', '儿童玩具'
                    ];
                    const randomIndex = Math.floor(Math.random() * thingNames.length);
                    generatedString = thingNames[randomIndex];
                } else {
                    // 其他关键词生成随机字符串
                    const timestamp = new Date().getTime();
                    const randomStr = Math.random().toString(36).substr(2, 6).toUpperCase();
                    generatedString = `T${timestamp}${randomStr}`.substr(0, 20);
                }

                this.taskForm.keywords[keyword] = generatedString;
                this.$message.success(`已自动生成${keyword}`);
            }
        },
        // 处理用户选择模式变化
        handleUserSelectionChange(value) {
            if (value === 1) {
                // 全选所有用户时，清空其他选择
                this.taskForm.tag_ids = [];
                this.taskForm.openids = [];
                this.selectedUserList = [];
                this.openidText = '';
            }
        },

        // 打开用户选择器
        openUserSelector() {
            if (!this.taskForm.account_id) {
                this.$message.warning('请先选择公众号账户');
                return;
            }
            this.showUserSelectorDialog = true;
        },



        // 处理OpenID输入
        handleOpenidInput(text) {
            this.openidText = text;
            const openids = text.split('\n')
                .map(openid => openid.trim())
                .filter(openid => openid.length > 0);
            this.taskForm.openids = openids;

            // 更新选中用户列表显示（只显示前10个）
            this.selectedUserList = openids.slice(0, 10).map(openid => ({ openid }));
        },

        // 移除选中的用户
        removeSelectedUser(openid) {
            const index = this.taskForm.openids.indexOf(openid);
            if (index > -1) {
                this.taskForm.openids.splice(index, 1);
                this.selectedUserList = this.selectedUserList.filter(user => user.openid !== openid);
                this.openidText = this.taskForm.openids.join('\n');
            }
        },

        // 清空选中用户
        clearSelectedUsers() {
            this.taskForm.openids = [];
            this.selectedUserList = [];
            this.openidText = '';
        },

        // 添加时间段
        addTimePeriod() {
            this.taskForm.time_periods.push({
                start_time: '09:00',
                end_time: '12:00',
                max_deliveries: 3
            });
        },

        // 删除时间段
        removeTimePeriod(index) {
            if (this.taskForm.time_periods.length > 1) {
                this.taskForm.time_periods.splice(index, 1);
            } else {
                this.$message.warning('至少需要保留一个时间段');
            }
        },

        // 打开编辑对话框
        async openEditDialog(task) {
            this.isEditMode = true;

            // 先设置账户ID，触发获取模板和标签
            this.taskForm.account_id = task.account_id;

            // 等待数据加载完成
            await Promise.all([
                this.fetchTemplates(),
                this.fetchTags()
            ]);

            // 设置任务数据
            this.taskForm = {
                ...task,
                status: task.status === 1,
                keywords: typeof task.keywords === 'string' ? JSON.parse(task.keywords) : task.keywords,
                tag_ids: typeof task.tag_ids === 'string' ? JSON.parse(task.tag_ids) : task.tag_ids,
                openids: typeof task.openids === 'string' ? JSON.parse(task.openids) : task.openids,
                time_periods: typeof task.time_periods === 'string' ? JSON.parse(task.time_periods) : task.time_periods,
                weekdays: typeof task.weekdays === 'string' ? JSON.parse(task.weekdays) : task.weekdays
            };

            // 设置选中的模板
            if (task.template_id) {
                const template = this.templateList.find(t => t.template_id === task.template_id);
                this.selectedTemplate = template;
            }

            // 设置用户选择显示
            if (this.taskForm.select_all === 1) {
                this.userSelectionMode = 'all';
            } else if (this.taskForm.tag_ids && this.taskForm.tag_ids.length > 0) {
                this.userSelectionMode = 'tags';
            } else if (this.taskForm.openids && this.taskForm.openids.length > 0) {
                this.userSelectionMode = 'openids';
                this.openidText = this.taskForm.openids.join('\n');
                this.selectedUserList = this.taskForm.openids.slice(0, 10).map(openid => ({ openid }));
            }

            this.showCreateDialog = true;
        },

        // 提交任务
        async submitTask() {
            // 表单验证
            if (!this.taskForm.task_name.trim()) {
                this.$message.warning('请输入任务名称');
                return;
            }

            if (!this.taskForm.account_id) {
                this.$message.warning('请选择公众号账户');
                return;
            }

            if (!this.taskForm.template_id) {
                this.$message.warning('请选择消息模板');
                return;
            }

            // 验证关键词
            const missingKeywords = this.templateKeywords.filter(keyword => !this.taskForm.keywords[keyword] || this.taskForm.keywords[keyword].trim() === '');
            if (missingKeywords.length > 0) {
                this.$message.warning(`请填写关键词: ${missingKeywords.join(', ')}`);
                return;
            }

            // 验证用户选择
            if (this.taskForm.select_all === 0) {
                if (this.userSelectionMode === 'tags' && (!this.taskForm.tag_ids || this.taskForm.tag_ids.length === 0)) {
                    this.$message.warning('请至少选择一个标签');
                    return;
                }
                if (this.userSelectionMode === 'openids' && (!this.taskForm.openids || this.taskForm.openids.length === 0)) {
                    this.$message.warning('请至少选择一个用户');
                    return;
                }
            }

            if (this.taskForm.weekdays.length === 0) {
                this.$message.warning('请至少选择一个发送星期');
                return;
            }

            if (this.taskForm.time_periods.length === 0) {
                this.$message.warning('请至少设置一个发送时间段');
                return;
            }

            this.submitting = true;
            try {
                const formData = {
                    ...this.taskForm,
                    store_id: this.userInfo.id,
                    status: this.taskForm.status ? 1 : 0,
                    keywords: JSON.stringify(this.taskForm.keywords),
                    tag_ids: JSON.stringify(this.taskForm.tag_ids),
                    openids: JSON.stringify(this.taskForm.openids),
                    time_periods: JSON.stringify(this.taskForm.time_periods),
                    weekdays: JSON.stringify(this.taskForm.weekdays)
                };

                const action = this.isEditMode ? 'update_task' : 'create_task';
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, {
                    action: action,
                    ...formData
                });

                if (response.data.success) {
                    this.$message.success(this.isEditMode ? '任务更新成功' : '任务创建成功');
                    this.showCreateDialog = false;
                    this.fetchTaskList();
                    this.fetchStats();
                    this.resetTaskForm();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                this.$message.error('操作失败');
                console.error('提交任务错误:', error);
            } finally {
                this.submitting = false;
            }
        },

        // 重置表单
        resetTaskForm() {
            this.taskForm = {
                task_name: '',
                account_id: '',
                template_id: '',
                keywords: {},
                url: '',
                select_all: 0,
                tag_ids: [],
                openids: [],
                send_count_per_delivery: 10,
                time_periods: [
                    { start_time: '09:00', end_time: '12:00', max_deliveries: 3 },
                    { start_time: '14:00', end_time: '18:00', max_deliveries: 3 }
                ],
                weekdays: [1, 2, 3, 4, 5],
                status: true,
                notes: ''
            };
            this.selectedTemplate = null;
            this.userSelectionMode = 'tags';
            this.openidText = '';
            this.selectedUserList = [];
        },

        // 切换任务状态
        async toggleTaskStatus(task) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, {
                    action: 'toggle_task_status',
                    store_id: this.userInfo.id,
                    task_id: task.id,
                    status: task.status ? 0 : 1
                });

                if (response.data.success) {
                    this.$message.success(task.status ? '任务已暂停' : '任务已启用');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                this.$message.error('操作失败');
                console.error('切换任务状态错误:', error);
            }
        },

        // 删除任务
        async deleteTask(taskId) {
            try {
                await this.$confirm('确定要删除这个微信定时任务吗？', '确认删除', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, {
                    action: 'delete_task',
                    store_id: this.userInfo.id,
                    task_id: taskId
                });

                if (response.data.success) {
                    this.$message.success('任务删除成功');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '删除失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('删除失败');
                    console.error('删除任务错误:', error);
                }
            }
        },

        // 立即发送
        async manualDelivery(task) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, {
                    action: 'manual_delivery',
                    store_id: this.userInfo.id,
                    task_id: task.id
                });

                if (response.data.success) {
                    this.$message.success('消息发送完成');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '发送失败');
                }
            } catch (error) {
                this.$message.error('发送失败');
                console.error('立即发送错误:', error);
            }
        },

        // 打开日志对话框
        openLogsDialog() {
            this.showLogsDialog = true;
        },

        // 获取投放日志
        async fetchDeliveryLogs() {
            this.logsLoading = true;
            try {
                const params = {
                    action: 'get_delivery_logs',
                    store_id: this.userInfo.id,
                    page: this.logsPagination.page,
                    page_size: this.logsPagination.pageSize
                };

                if (this.logsFilter.task_id) {
                    params.task_id = this.logsFilter.task_id;
                }
                if (this.logsFilter.date_range && this.logsFilter.date_range.length === 2) {
                    params.start_date = this.logsFilter.date_range[0];
                    params.end_date = this.logsFilter.date_range[1];
                }

                const response = await axios.post(`${config.BASE_URL}/shop/api/wechat_scheduled.php`, params);

                if (response.data.data) {
                    this.deliveryLogs = response.data.data;
                    this.logsPagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                this.$message.error('获取发送日志失败');
                console.error('获取发送日志错误:', error);
            } finally {
                this.logsLoading = false;
            }
        },

        // 重置日志筛选
        resetLogsFilter() {
            this.logsFilter = {
                task_id: '',
                date_range: []
            };
            this.fetchDeliveryLogs();
        },

        // 查看日志详情
        viewLogDetail(log) {
            this.currentLogDetail = log;
            this.showLogDetailDialog = true;
        },

        // 工具函数
        formatTime(time) {
            if (!time) return '-';
            return new Date(time).toLocaleString('zh-CN');
        }
    }
}
</script>

<style scoped lang="scss">
.wechat-scheduled-page {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.header-section {
    margin-bottom: 24px;
}

.stats-section {
    margin-bottom: 24px;
}

.stat-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        font-size: 24px;
        color: white;

        &.total {
            background: linear-gradient(135deg, #409EFF, #67C2EF);
        }

        &.active {
            background: linear-gradient(135deg, #67C23A, #85CE61);
        }

        &.success {
            background: linear-gradient(135deg, #E6A23C, #EBB563);
        }

        &.today {
            background: linear-gradient(135deg, #909399, #A6A9AD);
        }
    }

    .stat-content {
        flex: 1;

        .stat-number {
            font-size: 24px;
            font-weight: 700;
            color: #303133;
            margin-bottom: 4px;
        }

        .stat-label {
            font-size: 14px;
            color: #909399;
        }
    }
}

.action-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.task-list-section {
    margin-top: 20px;
}

.template-info {
    .template-name {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .template-id {
        color: #909399;
        font-size: 12px;
    }
}

.user-selection-info {
    .selection-type {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .count {
        font-size: 12px;
        color: #909399;
    }
}

.delivery-config,
.delivery-stats {
    font-size: 12px;
    color: #606266;
    line-height: 1.4;
}

.next-time {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 任务表单样式 */
.task-form {
    .template-preview {
        margin: 15px 0;
        border: 1px solid #ebeef5;
        border-radius: 6px;

        :deep(.el-collapse) {
            border: none;
        }

        .preview-content {
            padding: 15px;

            .template-title {
                margin-bottom: 10px;
                color: #303133;
            }

            .template-content,
            .template-example {
                margin-bottom: 10px;
                line-height: 1.5;

                .keyword {
                    color: #409eff;
                    background: #ecf5ff;
                    padding: 2px 6px;
                    border-radius: 3px;
                    margin: 0 2px;
                }
            }
        }
    }

    .keywords-container {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .keyword-item {
            :deep(.el-input-group__prepend) {
                background: #f5f7fa;
                color: #606266;
                font-weight: 500;
                min-width: 80px;
            }
        }
    }

    .user-selection-container {
        .custom-selection {
            margin-top: 15px;
            border: 1px solid #ebeef5;
            border-radius: 6px;
            padding: 15px;

            .selection-tabs {
                margin-bottom: 15px;
            }

            .tags-selection {
                .tags-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;

                    .selected-count {
                        font-size: 12px;
                        color: #909399;
                    }
                }

                .tags-list {
                    max-height: 200px;
                    overflow-y: auto;
                    padding: 10px;
                    background: #fafafa;
                    border-radius: 4px;

                    .tag-checkbox {
                        display: block;
                        margin-bottom: 8px;
                    }
                }

                .empty-tags {
                    margin-top: 10px;
                }
            }

            .openids-selection {
                .openids-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 10px;
                }

                .selected-users {
                    margin-top: 15px;
                    border-top: 1px solid #ebeef5;
                    padding-top: 10px;

                    .selected-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 10px;
                    }

                    .user-tags {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 8px;
                        max-height: 100px;
                        overflow-y: auto;

                        .el-tag {
                            max-width: 200px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }
                }
            }
        }
    }

    .weekdays-container {
        .el-checkbox-group {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
        }
    }

    .time-periods-container {
        border: 1px solid #ebeef5;
        border-radius: 6px;
        padding: 16px;
        background: #fafafa;
    }

    .time-periods-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        font-weight: 500;
        color: #606266;
    }

    .time-periods-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .period-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: white;
        border-radius: 4px;
        border: 1px solid #e4e7ed;
    }

    .period-time {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .time-separator {
        color: #909399;
        font-size: 14px;
    }

    .period-count {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .count-label {
        font-size: 12px;
        color: #909399;
        white-space: nowrap;
    }

    .form-tip {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
    }
}

/* 表格样式优化 */
.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

.user-info {
    .user-nickname {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .user-phone {
        color: #909399;
        font-size: 12px;
    }
}

.action-buttons {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;

    .el-button {
        flex: 1;
        min-width: 60px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;

        .el-icon {
            margin-right: 2px;
        }
    }
}

/* 日志相关样式 */
.logs-content {
    .logs-filter {
        margin-bottom: 16px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 6px;
    }
}

.fail-list {
    margin-top: 16px;

    h4 {
        margin-bottom: 8px;
        color: #606266;
    }

    .fail-tag {
        margin: 4px;
        max-width: 100%;
        word-break: break-all;
    }
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .settings-form {
        max-width: 100%;
        padding: 20px;
    }

    .period-item {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .period-time,
    .period-count {
        justify-content: center;
    }

    .action-buttons {
        gap: 4px;

        .el-button {
            min-width: 50px;
            font-size: 11px;

            .el-icon {
                margin-right: 1px;
            }
        }
    }
}

/* 用户选择器弹窗样式 */
.user-selector-dialog {
    :deep(.el-dialog__body) {
        padding: 0 20px;
        max-height: 70vh;
        overflow: hidden;
    }
}
</style>